"use client";

import { useTranslations, useLocale } from "next-intl";
import Footer from "@/components/Footer";
import PromotionalBanner from "@/components/PromotionalBanner";
import Image from "next/image";
import Link from "next/link";

export default function KnowledgePdsaPage() {
  const t = useTranslations();
  const locale = useLocale();
  const lang = locale.toUpperCase();
  const img = (n: number) => `/images/knowledge-pdsa/PDSA-${lang}-${n}.png`;

  const safetyTips = [
    {
      id: 1,
      title: t("pdsa.tip1Title"),
      description: t("pdsa.tip1Desc"),
      image: img(1),
    },
    {
      id: 2,
      title: t("pdsa.tip2Title"),
      description: t("pdsa.tip2Desc"),
      descriptionNote: t("pdsa.tip2Note"),
      image: img(2),
    },
    {
      id: 3,
      title: t("pdsa.tip3Title"),
      description: t("pdsa.tip3Desc"),
      image: img(3),
    },
    {
      id: 4,
      title: t("pdsa.tip4Title"),
      description: t("pdsa.tip4Desc"),
      image: img(4),
    },
    {
      id: 5,
      title: t("pdsa.tip5Title"),
      description: t("pdsa.tip5Desc"),
      legalNote: t("pdsa.tip5Legal"),
      image: img(5),
    },
    {
      id: 6,
      title: t("pdsa.tip6Title"),
      description: t("pdsa.tip6Desc"),
      image: img(6),
    },
  ];

  return (
    <main className="min-h-screen bg-white">
      
      
      <section className="lg:hidden bg-white px-6 pt-6 pb-2">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
          <h1 className="text-[32px] font-bold text-dark leading-[32px]">
            {t("knowledge.pdsaTitle")}
          </h1>
        </div>
      </section>
      <section className="hidden lg:block relative w-full overflow-hidden bg-white">
        <div className="relative w-full aspect-[8/1]">
          <Image
            src="/images/cover/cover-safetytips.jpg"
            alt="6 Safety Tips Banner"
            fill
            className="object-cover object-center w-full"
            sizes="100vw"
            priority
            quality={90}
          />
          
          <div className="absolute inset-0 px-6 lg:px-[clamp(2rem,5vw,250px)] flex items-center">
            <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
              <h1 className="text-3xl md:text-4xl lg:text-[56px] font-bold text-white leading-tight md:leading-tight lg:leading-[56px]">
                {t("knowledge.pdsaTitle")}
              </h1>
            </div>
          </div>
        </div>
      </section>

      <div className="hidden lg:block px-6 lg:px-[clamp(2rem,5vw,250px)] pt-[10px] pb-4">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
        <nav className="flex items-center gap-2 text-xs text-dark">
          <Link href="/" className="hover:text-honda-red transition-colors">
            {t("common.home")}
          </Link>
          <img src="/images/icons/icon-breadcrumb.svg" alt="" className="w-[6px] h-[11px]" aria-hidden="true" />
          <Link href="/safetytips/safetyfirst" className="text-honda-red hover:text-honda-red-dark transition-colors">
            {t("knowledge.pdsaTitle")}
          </Link>
          </nav>
        </div>
      </div>

      <section className="py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
            <div className="relative w-full max-w-[940px] mx-auto rounded-lg overflow-hidden bg-black">
              <div className="relative w-full aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/sD0c9dpAoCE?start=1"
                  title={t("knowledge.pdsaSlogan")}
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="bg-honda-red px-3 sm:px-4 md:px-5 lg:px-6 py-2 sm:py-2.5 md:py-3">
                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3">
                  <span className="text-white text-lg sm:text-xl md:text-2xl font-bold">6</span>
                  <span className="text-white text-xs sm:text-sm md:text-base lg:text-lg font-medium">{t("knowledge.pdsaSlogan")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-0">
              {safetyTips.map((tip, index) => (
                <div key={tip.id} className="flex flex-col w-full">
                  <div className="relative w-full h-[250px] sm:h-[300px] md:h-[400px] lg:h-[500px] mb-0 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    <Image
                      src={tip.image}
                      alt={tip.title}
                      fill
                      className="object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.parentElement) {
                          target.parentElement.innerHTML = `
                            <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                              <p class="text-gray-400 text-sm">Image not available</p>
                            </div>
                          `;
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PromotionalBanner />

      <Footer />
    </main>
  );
}
